/* ============================================================================
   Runtime switches. Everything here is env-driven so that flipping from the
   simulated round to the real one is a config change, not a code change.

   .env.local
     VITE_DATA_MODE=live
     VITE_COMPETITION_WS=wss://your-backend/utc/stream
     VITE_COMPETITION_REST=https://your-backend/utc
     VITE_PRICE_FEED=binance            # binance | custom | none
     VITE_PRICE_FEED_URL=...            # required when VITE_PRICE_FEED=custom
   ========================================================================== */

export type DataMode = 'mock' | 'live'

const env = ((import.meta as any).env ?? {}) as Record<string, string | undefined>

const num = (v: string | undefined, fallback: number): number => {
  const n = Number(v)
  return Number.isFinite(n) && v !== undefined && v !== '' ? n : fallback
}

export const RUNTIME = {
  mode: (env.VITE_DATA_MODE === 'live' ? 'live' : 'mock') as DataMode,

  /** Use the real price rail even while the agents are simulated, and anchor
      the simulated markets to those prices so the tape cannot contradict it. */
  livePrices: (env.VITE_LIVE_PRICES ?? 'true') !== 'false',

  /** Gate on the admin route. Client-side only -- see the README: real
      enforcement has to live in the backend that owns the round. */
  adminKey: env.VITE_ADMIN_KEY ?? 'utc-admin',

  /** Public handle shown in the header. */
  social: {
    handle: env.VITE_X_HANDLE ?? 'theutcfun',
    url: `https://x.com/${env.VITE_X_HANDLE ?? 'theutcfun'}`,
  },

  /** Seed for the simulated round. Same seed, same competition, every reload. */
  seed: num(env.VITE_SEED, 1337),

  /** Volatility and activity multiplier for the simulator. Time stays 1:1. */
  simSpeed: num(env.VITE_SIM_SPEED, 2),

  /** Wall-clock cadence of the mark-to-market loop. */
  tickMs: num(env.VITE_TICK_MS, 250),

  /** Minutes of price history warmed up when a round opens. Prices only --
      no trades, no positions, nothing that did not happen. */
  warmupMinutes: num(env.VITE_WARMUP_MINUTES, 120),

  /** Equity samples retained in memory. */
  maxEquitySamples: num(env.VITE_MAX_EQUITY_SAMPLES, 3000),
  maxTrades: num(env.VITE_MAX_TRADES, 220),
  maxThoughts: num(env.VITE_MAX_THOUGHTS, 160),

  live: {
    wsUrl: env.VITE_COMPETITION_WS ?? '',
    restUrl: env.VITE_COMPETITION_REST ?? '',
    priceFeed: (env.VITE_PRICE_FEED ?? 'binance') as 'binance' | 'custom' | 'none',
    priceFeedUrl: env.VITE_PRICE_FEED_URL ?? '',
    /** Treat the feed as stalled after this long without a message. */
    stallMs: num(env.VITE_STALL_MS, 15_000),
    reconnectBaseMs: num(env.VITE_RECONNECT_MS, 1_000),
  },
} as const
